import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './clientFeedbackView.css';

// ── XML helpers ──────────────────────────────────────────────────────────────

function applyMarkStyling(html) {
  if (!html) return '';
  let out = html;
  out = out.replace(/<span class="EnSpace"(\s*\/|><\/span)>/g, ' ');
  out = out.replace(/<\?xm-deletion_mark data="([\s\S]+?)"\?>/g, (_, data) => {
    const div = document.createElement('div');
    div.innerHTML = data.replace(/\\"/g, '"');
    return `<span class="cfv-deletion">${div.textContent}</span>`;
  });
  out = out.replace(/<\?xm-insertion_mark_start\s*\?>([\s\S]*?)<\?xm-insertion_mark_end\s*\?>/g, '<span class="cfv-insertion">$1</span>');
  out = out.replace(/<\/?(xhtml|shtml):[^>]*>/g, '');
  out = out.replace(/ xmlns="http:\/\/www.w3.org\/1999\/xhtml"/g, '');
  return out;
}

function parseBillXml(xmlContent) {
  if (!xmlContent) return { digestLine: '', digestHtml: '', contentHtml: '' };
  try {
    let parsable = xmlContent
      .replace(/<\?xm-deletion_mark\s+data="([\s\S]*?)"\s*\?>/g, (_, d) => {
        let decoded = d
          .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"')
          .replace(/&apos;/g, "'").replace(/&amp;/g, '&').replace(/\\"/g, '"');
        return `<span class="cfv-deletion">${decoded}</span>`;
      })
      .replace(/<\?xm-insertion_mark_start\s*\?>/g, '<span class="cfv-insertion">')
      .replace(/<\?xm-insertion_mark_end\s*\?>/g, '</span>');

    const parser = new DOMParser();
    const doc = parser.parseFromString(parsable, 'text/xml');

    const get = (tag) => {
      const el = doc.getElementsByTagName('caml:' + tag)[0] || doc.getElementsByTagName(tag)[0];
      if (!el) return '';
      const s = new XMLSerializer();
      return applyMarkStyling(Array.from(el.childNodes).map(c => s.serializeToString(c)).join('').trim());
    };

    const getInner = (el) => {
      if (!el) return '';
      const s = new XMLSerializer();
      return applyMarkStyling(Array.from(el.childNodes).map(c => s.serializeToString(c)).join(''));
    };

    const historyNode = doc.getElementsByTagName('caml:History')[0] || doc.getElementsByTagName('History')[0];
    const actionNodes = historyNode
      ? (historyNode.getElementsByTagName('caml:Action').length
          ? historyNode.getElementsByTagName('caml:Action')
          : historyNode.getElementsByTagName('Action'))
      : [];
    const isAmended = Array.from(actionNodes).some(a => {
      const text = a.getElementsByTagName('caml:ActionText')[0]?.textContent || a.getElementsByTagName('ActionText')[0]?.textContent || '';
      return text.toUpperCase().includes('AMENDED');
    });

    const measureType = get('MeasureType');
    const measureNum = get('MeasureNum');
    const digestAuthor = get('Name');
    const subject = get('Subject');
    const digestLine = `${measureType} ${measureNum}, ${isAmended ? 'as amended' : 'as introduced'}, ${digestAuthor}. ${subject}`;

    const digestNode = doc.getElementsByTagName('caml:DigestText')[0] || doc.getElementsByTagName('DigestText')[0];
    let digestHtml = digestNode ? new XMLSerializer().serializeToString(digestNode) : '';
    digestHtml = digestHtml.replace(/^<[^>]+>|<\/[^>]+>$/g, '');
    digestHtml = applyMarkStyling(digestHtml);

    const renderNode = (node) => {
      if (node.nodeType === 3) return applyMarkStyling(node.textContent);
      if (node.nodeType !== 1) return '';
      const tag = node.tagName.replace(/caml:/i, '');
      let html = '';
      switch (tag) {
        case 'Preamble':
          return `<div class="mb-3 text-uppercase">${getInner(node)}</div>`;
        case 'BillSection': {
          const numHtml = getInner(node.querySelector(':scope > Num, :scope > caml\\:Num'));
          const actionLineHtml = getInner(node.querySelector(':scope > ActionLine, :scope > caml\\:ActionLine'));
          const bodyNodes = Array.from(node.childNodes).filter(c => {
            if (c.nodeType !== 1) return true;
            const t = c.tagName.replace(/caml:/i, '');
            return !['Num', 'ActionLine'].includes(t);
          });
          let childrenHtml = bodyNodes.map(renderNode).join('');
          html += `<div class="cfv-bill-section">`;
          if (actionLineHtml) {
            html += `<p><strong>${numHtml}</strong> ${actionLineHtml}</p>${childrenHtml}`;
          } else {
            if (childrenHtml.trim().startsWith('<p>')) {
              childrenHtml = childrenHtml.replace('<p>', `<p><strong>${numHtml}</strong> `);
            } else {
              childrenHtml = `<p><strong>${numHtml}</strong></p>${childrenHtml}`;
            }
            html += childrenHtml;
          }
          html += `</div>`;
          break;
        }
        case 'Fragment':
          Array.from(node.children).forEach(c => { html += renderNode(c); });
          break;
        case 'LawSection': {
          const lNum = getInner(node.querySelector(':scope > Num, :scope > caml\\:Num'));
          const ver = node.querySelector('LawSectionVersion, caml\\:LawSectionVersion') || node;
          const content = getInner(ver.querySelector('Content, caml\\:Content'));
          html += `<div class="cfv-law-section">`;
          if (content.trim().startsWith('<p>')) {
            html += content.replace('<p>', `<p><strong>${lNum}</strong> `);
          } else {
            html += `<p><strong>${lNum}</strong></p>${content}`;
          }
          html += `</div>`;
          break;
        }
        case 'span':
          if (node.classList.contains('cfv-insertion') || node.classList.contains('cfv-deletion')) {
            html += `<span class="${node.getAttribute('class')}">${Array.from(node.childNodes).map(renderNode).join('')}</span>`;
          }
          break;
        default:
          html = Array.from(node.childNodes).map(renderNode).join('');
      }
      return html;
    };

    const billNode = doc.querySelector('Bill, caml\\:Bill, Resolution, caml\\:Resolution');
    let contentHtml = '';
    if (billNode) {
      contentHtml = Array.from(billNode.childNodes).map(renderNode).join('');
    }

    return { digestLine, digestHtml, contentHtml };
  } catch {
    return { digestLine: '', digestHtml: '', contentHtml: '' };
  }
}

// ── Notes renderer ───────────────────────────────────────────────────────────

function renderNotesWithQuotes(text) {
  if (!text?.trim()) return null;
  return text.split(/\n\n+/).map((block, i) => {
    const trimmed = block.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.length > 2) {
      return (
        <blockquote key={i} className="cfv-notes-quote">
          {trimmed.slice(1, -1)}
        </blockquote>
      );
    }
    const lines = trimmed.split('\n');
    return (
      <p key={i} className="cfv-notes-para">
        {lines.map((line, j) => (
          <span key={j}>{line}{j < lines.length - 1 && <br />}</span>
        ))}
      </p>
    );
  }).filter(Boolean);
}

// ── Bill list item ───────────────────────────────────────────────────────────

function BillListItem({ bill, isSelected, isSaved, onClick }) {
  return (
    <button
      className={`cfv-list-item${isSelected ? ' cfv-list-item--active' : ''}`}
      onClick={onClick}
    >
      <div className="cfv-list-item-top">
        <span className="cfv-list-item-num">{bill.bill_type} {bill.bill_number}</span>
        {isSaved && <i className="bi bi-check-circle-fill cfv-list-saved-icon" title="Notes saved" />}
      </div>
      {bill.title && <div className="cfv-list-item-title">{bill.title}</div>}
    </button>
  );
}

// ── Reading pane ─────────────────────────────────────────────────────────────

function BillReadingPane({ bill }) {
  const { digestLine, digestHtml, contentHtml } = useMemo(
    () => parseBillXml(bill?.LatestVersion?.version_copy),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bill?.bill_id]
  );
  const [showFullText, setShowFullText] = useState(false);

  useEffect(() => { setShowFullText(false); }, [bill?.bill_id]);

  return (
    <div className="cfv-reading-content">
      <div className="cfv-bill-header">
        <span className="cfv-bill-number">{bill.bill_type} {bill.bill_number}</span>
        {bill.LocationCode?.description && (
          <span className="cfv-bill-location">{bill.LocationCode.description}</span>
        )}
      </div>

      {bill.title && <div className="cfv-bill-title">{bill.title}</div>}

      <div className="cfv-bill-text">
        {digestLine && (
          <p className="cfv-digest-line" dangerouslySetInnerHTML={{ __html: digestLine }} />
        )}
        {digestHtml && (
          <div className="cfv-digest-body" dangerouslySetInnerHTML={{ __html: digestHtml }} />
        )}
      </div>

      {contentHtml && (
        <div className="cfv-fulltext-section">
          <button
            className="cfv-fulltext-toggle"
            onClick={() => setShowFullText(v => !v)}
          >
            <i className={`bi bi-chevron-${showFullText ? 'up' : 'down'} me-1`} />
            {showFullText ? 'Hide full bill text' : 'View full bill text'}
          </button>
          {showFullText && (
            <div className="cfv-fulltext-body" dangerouslySetInnerHTML={{ __html: contentHtml }} />
          )}
        </div>
      )}
    </div>
  );
}

// ── Notes pane ───────────────────────────────────────────────────────────────

function BillNotesPane({ bill, notes, savedNotes, onChange, onSave, isSaving, justSaved, submitted }) {
  if (!bill) {
    return (
      <div className="cfv-notes-placeholder">
        <i className="bi bi-pencil-square cfv-notes-placeholder-icon" />
        <p>Select a bill to add notes</p>
      </div>
    );
  }

  const hasChanged = notes !== savedNotes;

  return (
    <div className="cfv-notes-inner">
      <div className="cfv-notes-header">
        <span className="cfv-notes-label">Notes</span>
        <span className="cfv-notes-bill-tag">{bill.bill_type} {bill.bill_number}</span>
      </div>

      {submitted ? (
        <div className="cfv-submitted-notes">
          {notes ? renderNotesWithQuotes(notes) : <em className="text-muted">No notes provided.</em>}
        </div>
      ) : (
        <>
          <p className="cfv-notes-hint">
            <i className="bi bi-quote me-1" />
            Highlight text in the bill to quote it in your notes
          </p>
          <textarea
            className="cfv-notes-textarea"
            rows={10}
            placeholder="Share your thoughts on this bill…"
            value={notes}
            onChange={e => onChange(e.target.value)}
          />
          <div className="cfv-notes-actions">
            {justSaved && (
              <span className="cfv-saved-confirm">
                <i className="bi bi-check-circle me-1" />Saved
              </span>
            )}
            <button
              className="cfv-save-btn"
              onClick={() => onSave(bill.bill_id, notes)}
              disabled={isSaving || !hasChanged}
            >
              {isSaving ? 'Saving…' : 'Save Notes'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Page header ──────────────────────────────────────────────────────────────

function PageHeader({ request }) {
  const displayName = request?.org_name || request?.team_name || '';
  const logo = request?.org_logo;
  return (
    <div className="cfv-header">
      <div className="cfv-header-brand">
        {logo && <img src={logo} alt={displayName} className="cfv-header-logo" />}
        <span className="cfv-header-org-name">{displayName}</span>
      </div>
      <div className="cfv-header-poweredby">
        Feedback portal provided by <strong>'elloGovna</strong>
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function ClientFeedbackView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [savedBillNotes, setSavedBillNotes] = useState({});
  const [savingBills, setSavingBills] = useState({});
  const [justSaved, setJustSaved] = useState({});
  const [quoteSelection, setQuoteSelection] = useState(null);
  const readingPaneRef = useRef(null);

  useEffect(() => {
    axios.get(`/api/client-feedback/access/${token}`)
      .then(res => {
        setData(res.data);
        const map = {};
        res.data.responses.forEach(r => { map[r.bill_id] = r; });

        const drafts = {};
        const saved = {};
        res.data.bills.forEach(b => {
          const r = map[b.bill_id];
          drafts[b.bill_id] = r?.notes || '';
          saved[b.bill_id] = r?.notes || '';
        });
        setNoteDrafts(drafts);
        setSavedBillNotes(saved);

        if (res.data.bills.length > 0) {
          setSelectedBillId(res.data.bills[0].bill_id);
        }

        const allSubmitted = res.data.bills.length > 0 &&
          res.data.bills.every(b => map[b.bill_id]?.submitted_at);
        if (allSubmitted) setSubmitted(true);
      })
      .catch(err => {
        setError(err.response?.data?.msg || 'Failed to load feedback request.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = useCallback(async (bill_id, notes) => {
    setSavingBills(prev => ({ ...prev, [bill_id]: true }));
    try {
      await axios.post(`/api/client-feedback/respond/${token}`, { bill_id, notes });
      setSavedBillNotes(prev => ({ ...prev, [bill_id]: notes }));
      setJustSaved(prev => ({ ...prev, [bill_id]: true }));
      setTimeout(() => setJustSaved(prev => ({ ...prev, [bill_id]: false })), 2500);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSavingBills(prev => ({ ...prev, [bill_id]: false }));
    }
  }, [token]);

  const handleSubmitAll = async () => {
    setSubmitting(true);
    try {
      await axios.post(`/api/client-feedback/submit/${token}`);
      setSubmitted(true);
    } catch (err) {
      alert(err.response?.data?.msg || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Quote / highlight feature
  const handleReadingMouseUp = useCallback(() => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    if (!text || !readingPaneRef.current?.contains(sel.anchorNode)) return;
    try {
      const range = sel.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setQuoteSelection({
        text,
        x: Math.round(rect.left + rect.width / 2),
        y: Math.round(rect.top),
      });
    } catch {
      // ignore selection errors
    }
  }, []);

  const insertQuote = useCallback(() => {
    if (!quoteSelection || !selectedBillId) return;
    const quoted = `"${quoteSelection.text}"\n\n`;
    setNoteDrafts(prev => ({
      ...prev,
      [selectedBillId]: quoted + (prev[selectedBillId] || ''),
    }));
    setQuoteSelection(null);
    window.getSelection()?.removeAllRanges();
  }, [quoteSelection, selectedBillId]);

  useEffect(() => {
    if (!quoteSelection) return;
    const dismiss = (e) => {
      if (!e.target.closest('.cfv-quote-popup')) setQuoteSelection(null);
    };
    document.addEventListener('mousedown', dismiss);
    return () => document.removeEventListener('mousedown', dismiss);
  }, [quoteSelection]);

  const selectedBill = data?.bills.find(b => b.bill_id === selectedBillId) ?? null;

  if (loading) return (
    <div className="cfv-loading">
      <div className="spinner-border text-secondary" role="status" />
    </div>
  );

  if (error) return (
    <div className="cfv-page">
      <div className="cfv-shell">
        <PageHeader request={null} />
        <div className="cfv-error">
          <i className="bi bi-exclamation-circle cfv-error-icon" />
          <p>{error}</p>
        </div>
      </div>
    </div>
  );

  const expiry = new Date(data.request.expires_at).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  const savedCount = data.bills.filter(b => savedBillNotes[b.bill_id]?.trim()).length;

  return (
    <div className="cfv-page">
      <div className="cfv-shell">
        <PageHeader request={data.request} />

        <div className="cfv-greeting">
          <h1 className="cfv-greeting-title">
            Hello, {data.contact.first_name} {data.contact.last_name}
          </h1>
          <p className="cfv-greeting-sub">
            <strong>{data.request.org_name || data.request.team_name}</strong> has requested your
            feedback on {data.bills.length} bill{data.bills.length !== 1 ? 's' : ''} for{' '}
            <strong>{data.request.client_name}</strong>.
          </p>
          {data.request.message && (
            <blockquote className="cfv-message">{data.request.message}</blockquote>
          )}
          {!submitted && (
            <p className="cfv-expiry">
              <i className="bi bi-clock me-1" />
              This link expires on <strong>{expiry}</strong>.
            </p>
          )}
        </div>

        {submitted && (
          <div className="cfv-submitted-banner">
            <i className="bi bi-check-circle-fill me-2" />
            Thank you — your feedback has been submitted and the team has been notified.
          </div>
        )}

        {/* Three-column workspace */}
        <div className="cfv-workspace">

          {/* Left: bill list */}
          <div className="cfv-list-panel">
            <div className="cfv-list-panel-header">
              <span>Bills</span>
              <span className="cfv-list-progress">
                {savedCount}/{data.bills.length}
                <i className="bi bi-check-circle ms-1" />
              </span>
            </div>
            <div className="cfv-list-panel-items">
              {data.bills.map(bill => (
                <BillListItem
                  key={bill.bill_id}
                  bill={bill}
                  isSelected={selectedBillId === bill.bill_id}
                  isSaved={!!(savedBillNotes[bill.bill_id]?.trim())}
                  onClick={() => setSelectedBillId(bill.bill_id)}
                />
              ))}
            </div>
          </div>

          {/* Center: reading pane */}
          <div
            className="cfv-reading-panel"
            ref={readingPaneRef}
            onMouseUp={handleReadingMouseUp}
          >
            {selectedBill ? (
              <BillReadingPane bill={selectedBill} />
            ) : (
              <div className="cfv-reading-empty">
                <i className="bi bi-file-text cfv-reading-empty-icon" />
                <p>Select a bill from the list</p>
              </div>
            )}
          </div>

          {/* Right: notes pane */}
          <div className="cfv-notes-panel">
            <BillNotesPane
              bill={selectedBill}
              notes={selectedBillId != null ? (noteDrafts[selectedBillId] ?? '') : ''}
              savedNotes={selectedBillId != null ? (savedBillNotes[selectedBillId] ?? '') : ''}
              onChange={val => setNoteDrafts(prev => ({ ...prev, [selectedBillId]: val }))}
              onSave={handleSave}
              isSaving={!!savingBills[selectedBillId]}
              justSaved={!!justSaved[selectedBillId]}
              submitted={submitted}
            />
          </div>
        </div>

        {!submitted && (
          <div className="cfv-submit-row">
            <button
              className="cfv-submit-btn"
              onClick={handleSubmitAll}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit All Feedback'}
            </button>
            <p className="cfv-submit-note">
              Once submitted, your notes will be locked and sent to the team.
            </p>
          </div>
        )}
      </div>

      {/* Quote popup */}
      {quoteSelection && (
        <div
          className="cfv-quote-popup"
          style={{
            position: 'fixed',
            left: quoteSelection.x,
            top: quoteSelection.y,
            transform: 'translate(-50%, calc(-100% - 8px))',
          }}
        >
          <button className="cfv-quote-btn" onClick={insertQuote}>
            <i className="bi bi-quote me-1" />
            Quote in notes
          </button>
        </div>
      )}
    </div>
  );
}
